import PatientRoutes from '@lib/routes/PatientRoutes'
import { trackEvent } from '@lib/tracking'
import { TrackingEvents } from '@lib/tracking/TrackingEvents'
import type { ApplySuccessPageProps } from 'app/p/apply-success/[token]/[step]/page'
import { POST_APPLY_STEPS, type PostApplyStep } from './stepTypes'

type ApplySuccessRouteRedirectProps = Pick<
  ApplySuccessPageProps,
  | 'allowPatientCallToSite'
  | 'canPatientContactSite'
  | 'consentForEmr'
  | 'consentToShareYourStory'
  | 'hasPatientNavigatorAgentCallCompleted'
  | 'idToken'
  | 'isFullyQualified'
  | 'isQualifiedApply'
  | 'showPatientNavigatorAgentStep'
  | 'showScreeningVisitQualificationStep'
  | 'whyAreYouApplying'
>

/**
 * This function is used to determine the next step to redirect to during the post-apply steps. It uses
 * stepTypes.ts for the order and should map to Steps.tsx.
 *
 * @param currentStep - The current step of the post-apply process.
 * @param props - The props of the apply success page.
 * @returns The next step to redirect to.
 */
export const getApplySuccessRouteToRedirectTo = (
  currentStep: PostApplyStep,
  props: ApplySuccessRouteRedirectProps,
  userId?: number,
) => {
  const possibleNextSteps: PostApplyStep[] = getPossibleNextSteps(currentStep)
  const projectedSteps: PostApplyStep[] = possibleNextSteps.filter(
    (step) => !shouldSkipStep(currentStep, step, props),
  )

  trackEvent(
    TrackingEvents.POST_TRIAL_APPLY_NEXT_STEPS_COMPUTED,
    {
      currentStep,
      projectedSteps,
      val: JSON.stringify(props),
    },
    undefined,
    undefined,
    userId,
  )

  const maybeNextStep: PostApplyStep | undefined = projectedSteps[0]

  if (maybeNextStep) {
    return PatientRoutes.postApplyStep(props.idToken, maybeNextStep)
  }

  return PatientRoutes.applyPackage(props.idToken)
}

const shouldSkipStep = (
  currentStep: PostApplyStep,
  step: PostApplyStep,
  {
    allowPatientCallToSite,
    canPatientContactSite,
    consentForEmr,
    consentToShareYourStory,
    hasPatientNavigatorAgentCallCompleted,
    isFullyQualified,
    isQualifiedApply,
    showPatientNavigatorAgentStep,
    showScreeningVisitQualificationStep,
    whyAreYouApplying,
  }: ApplySuccessRouteRedirectProps,
) => {
  // Used for steps where the patient can always advance regardless of other prop values
  const isCurrentlyOnThisStep = currentStep === step

  switch (step) {
    case 'screening-visit-qualification':
      return isCurrentlyOnThisStep || !showScreeningVisitQualificationStep
    case 'patient-call-to-site-q':
      return (
        isCurrentlyOnThisStep ||
        !isFullyQualified ||
        !allowPatientCallToSite ||
        showScreeningVisitQualificationStep
      )
    case 'patient-navigator-agent':
      return (
        isCurrentlyOnThisStep ||
        hasPatientNavigatorAgentCallCompleted ||
        !showPatientNavigatorAgentStep ||
        showScreeningVisitQualificationStep
      ) // we already have a completed call or no agentId means patient should advance
    case 'medical-release-consent':
      // allow someone to skip this step if they've started or hit the "skip" button
      return (
        isCurrentlyOnThisStep ||
        (Boolean(consentForEmr) &&
          !['Error', 'Expired', 'Revoked'].includes(
            consentForEmr?.status ?? '',
          ))
      )
    case 'medical-release-signature':
      return (
        !consentForEmr ||
        consentForEmr?.status === 'Complete' ||
        consentForEmr?.status === 'Pending'
      )
    case 'patient-call-to-site':
      return (
        isCurrentlyOnThisStep ||
        !allowPatientCallToSite ||
        showScreeningVisitQualificationStep
      )
    case 'phone-call-availability':
      return (
        isCurrentlyOnThisStep ||
        !isQualifiedApply ||
        showScreeningVisitQualificationStep
      )
    case 'why-apply':
      return Boolean(whyAreYouApplying) || isCurrentlyOnThisStep // allow skipping why apply step
    case 'save-study-team-contact':
      return (
        isCurrentlyOnThisStep ||
        !canPatientContactSite ||
        showScreeningVisitQualificationStep
      )
    case 'share-trial':
      return isQualifiedApply || showScreeningVisitQualificationStep // skip the step if the patient is qualified for the trial, or if we're showing the new screening visit qualification flow instead
    case 'share-story':
      return isCurrentlyOnThisStep || Boolean(consentToShareYourStory)
    default:
      return false
  }
}

const getPossibleNextSteps = (currentStep: PostApplyStep): PostApplyStep[] => {
  const currentIndex = POST_APPLY_STEPS.indexOf(currentStep)

  return POST_APPLY_STEPS.slice(currentIndex)
}
